import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { GridConstants } from '@/common/grid/GridConstants';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { StringUtils } from '@/common/utils/StringUtils';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

export const EntitySelectDialog = ({ data, closeDialog }) => {
  const rsrc = useRsrc();

  const [entityDef, setEntityDef] = useState();
  const [selectedEntities, setSelectedEntities] = useState([]);

  const multiple = data.multiple;
  // Infinite model: a row can only be re-selected once its block has loaded
  const pendingIdsRef = useRef(new Set(data.selectedIds ?? []));

  useEffect(() => {
    rsrc.entityDef(data.module, data.entity).then((entityDef) => {
      setEntityDef(entityDef);
    });
  }, []);

  const augFieldDefs = (fieldToFieldDef) => {
    Object.values(fieldToFieldDef).forEach((fieldDef) => {
      if (['HYPERLINK', 'CODE'].includes(fieldDef.type)) {
        fieldDef.type = 'NONE';
      }
    });

    fieldToFieldDef.checkbox = GridConstants.checkbox;

    GridConstants.removeAuditFieldDefs(fieldToFieldDef);
  };

  const augRowsReq = useCallback((rowsReq) => {
    if (data.expr) {
      rowsReq.expr = ExprUtils.and(rowsReq.expr, data.expr);
    }
    rowsReq.query = data.query;
    rowsReq.replaceParams = data.replaceParams;
    // Default order until the user sorts a column
    if (!rowsReq.sortModel?.length && data.sortModel) {
      rowsReq.sortModel = data.sortModel;
    }
  }, []);

  const rowSelected = (event) => {
    const selectedRows = event.api.getSelectedRows();
    setSelectedEntities(selectedRows);
  };

  const restoreSelectedRows = (api) => {
    if (!pendingIdsRef.current.size) return;
    api.forEachNode((node) => {
      if (node.data && pendingIdsRef.current.delete(node.data.id)) node.setSelected(true);
    });
  };

  const submit = () => {
    if (!multiple) {
      closeDialog(selectedEntities);
      return;
    }

    // Nothing selected means no restriction (All)
    const selectedIds = [...selectedEntities.map((entity) => entity.id), ...pendingIdsRef.current];
    if (selectedIds.length === 0) {
      closeDialog({ expr: null, selectedCount: 0, selectedIds: [] });
      return;
    }
    closeDialog({ expr: ExprUtils.setExpr('id', selectedIds), selectedCount: selectedIds.length, selectedIds });
  };

  return (
    entityDef && (
      <Dialog onClose={() => closeDialog()} open={true} fullWidth maxWidth="lg">
        <DialogTitle>{'Select ' + StringUtils.toSingular(entityDef.displayName)}</DialogTitle>
        <DialogContent>
          <Box sx={{ height: '60vh' }}>
            <EntityList
              id={data.field}
              module={data.module}
              entity={data.entity}
              augFieldDefs={augFieldDefs}
              augRowsReq={augRowsReq}
              gridOptions={{
                rowSelection: multiple ? 'multiple' : 'single',
                suppressRowClickSelection: multiple,
                getRowId: (params) => String(params.data.id),
                // Show the default order as a real column sort; a sort saved from an earlier visit still wins
                initialState: data.sortModel ? { sort: { sortModel: data.sortModel } } : undefined,
              }}
              events={{
                onSelectionChanged: rowSelected,
                onModelUpdated: multiple ? (e) => restoreSelectedRows(e.api) : undefined,
              }}
              showTitle={false}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={submit}
            disabled={!multiple && selectedEntities.length === 0}
          >
            {multiple && selectedEntities.length === 0 && pendingIdsRef.current.size === 0 ? 'Select All' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    )
  );
};
